import ContactProfileImage from '@app/components/ContactProfileImage'
import { FeedItemSourceType, FeedItemType } from '@app/models/enums'
import { IFeedItem } from '@app/models/FeedItem'
import { ICurrentUserState } from '@app/store/modules/account'
import { bindFunctions } from '@app/utils/class'
import { formatDate } from '@app/utils/formatting'
import { humanize } from '@app/utils/formatting'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as React from 'react'
import { Link } from 'react-router-dom'
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from 'reactstrap'
import ShareFeedItemModal from '../containers/ShareFeedItemModalContainer'
import './FeedItem.scss'

interface IFeedItemHeaderProps {
  archiveLead: (feedItemId: string) => void
  beginEdit: (feedItemId: string) => void
  deleteFeedItem: (feedItemId: string) => void
  item: IFeedItem
  saveChangeType: (feedItemId: string, feedItemType: FeedItemType) => void
  showShareFeedItemModal: (feedItemId: string) => void
}

type FeedItemHeaderProps = IFeedItemHeaderProps & ICurrentUserState

export default class FeedItemHeader extends React.PureComponent<FeedItemHeaderProps> {
  constructor(props: IFeedItemHeaderProps) {
    super(props)

    bindFunctions(this)
  }

  public render() {
    const item = this.props.item
    const author = item.author

    return (
      <div className='feed-item-header text-light d-flex'>
        <Link className='mr-2' to={`/connection/${author.contactId}`}>
          <ContactProfileImage contact={author.contactId} size={40} />
        </Link>
        <div className='flex-grow-1 overflow-hidden'>
          <div>
            <Link
              className='font-weight-bold'
              to={`/connection/${author.contactId}`}
            >
              {author.name}
            </Link>
            {item.isEditing && <span className='ml-1 small'>(editing)</span>}
            {!item.isEditing && item.isEdited && (
              <span className='ml-1 small'>(edited)</span>
            )}
            {this._renderCreatedOn(item)}
          </div>
          <div className='small'>
            {formatDate(item.lastActivityTime, 'MMM D, YYYY h:mm a')}
            {item.lastActivityTime !== item.dateCreated && (
              <span className='d-none d-sm-inline-block ml-1'>
                (created on {formatDate(item.dateCreated, 'MMM D, YYYY h:mm a')}
                )
              </span>
            )}
            {item.isPublic && (
              <span className='ml-1 text-primary' title='Visible to Public'>
                <FontAwesomeIcon icon={['far', 'lock-open']} fixedWidth />
              </span>
            )}
          </div>
        </div>
        <div>
          <UncontrolledDropdown>
            <DropdownToggle tag='a' className='px-1 text-center'>
              <FontAwesomeIcon icon={['fal', 'chevron-down']} fixedWidth />
            </DropdownToggle>
            {this._renderDropdown()}
          </UncontrolledDropdown>
        </div>
      </div>
    )
  }

  private _archiveLead() {
    this.props.archiveLead(this.props.item.id)
  }

  private _beginEdit() {
    this.props.beginEdit(this.props.item.id)
  }

  private _canArchive() {
    if (!this.props.user) {
      return false
    }

    if (this.props.item.isArchived === true) {
      return false
    }

    if (this.props.item.type === FeedItemType.Lead) {
      return (
        this.props.user.permissions.isAdmin ||
        this.props.user.id === this.props.item.author.contactId ||
        this.props.item.owners.contains(this.props.user.id)
      )
    }

    return this.props.user.id === this.props.item.author.contactId
  }

  private _canDelete() {
    if (!this.props.user) {
      return false
    }

    return (
      this.props.user.permissions.isAdmin === true ||
      this.props.user.id === this.props.item.author.contactId ||
      (this.props.item.createdOn &&
        this.props.user.id === this.props.item.createdOn.id)
    )
  }

  private _canEdit() {
    if (!this.props.user) {
      return false
    }

    // leads have different editing rules
    if (this.props.item.type === FeedItemType.Lead) {
      return (
        this.props.user.permissions.isAdmin ||
        this.props.user.id === this.props.item.author.contactId ||
        this.props.item.owners.contains(this.props.user.id)
      )
    }

    return this.props.user.id === this.props.item.author.contactId
  }

  private _deleteFeedItem() {
    this.props.deleteFeedItem(this.props.item.id)
  }

  private _renderCreatedOn(item: IFeedItem) {
    const createdOnClasses =
      'feed-item-created-on small text-truncate float-md-right'

    if (!item.createdOn || item.author.contactId === item.createdOn.id) {
      return (
        <div className={createdOnClasses}>
          {item.type === FeedItemType.Lead
            ? `${item.isArchived ? 'Archived' : ''} ${item.type} (${humanize(
                item.leadType,
              )})`
            : item.type}
        </div>
      )
    } else {
      return (
        <div className={createdOnClasses}>
          {item.type === FeedItemType.Lead
            ? `${item.isArchived ? 'Archived' : ''} ${item.type} (${humanize(
                item.leadType,
              )})`
            : item.type}{' '}
          on{' '}
          <Link to={this._renderRedirectLink(item)}>{item.createdOn.name}</Link>
        </div>
      )
    }
  }

  private _renderRedirectLink(item: IFeedItem) {
    if (!item.createdOn) {
      return ''
    }

    let baseUrl = ''

    switch (item.createdOn.type) {
      case FeedItemSourceType.Assignment:
        baseUrl = '/assignment'
        break
      case FeedItemSourceType.Contact:
        baseUrl = '/connection'
        break
      case FeedItemSourceType.DevelopmentSite:
        baseUrl = '/development/sites'
        break
      case FeedItemSourceType.Property:
        baseUrl = '/properties/property'
        break
      case FeedItemSourceType.Team:
        baseUrl = '/team'
        break
    }

    return `${baseUrl}/${item.createdOn.id}`
  }

  private _renderDropdown() {
    const menuItems = []
    const item = this.props.item
    if (!item.isEditing) {
      menuItems.push(
        <DropdownItem key='share' onClick={this._share}>
          Share
          <ShareFeedItemModal feedItemId={this.props.item.id} />
        </DropdownItem>,
      )

      if (this._canEdit()) {
        menuItems.push(
          <DropdownItem
            disabled={item.isSaving}
            key='edit'
            onClick={this._beginEdit}
          >
            Edit
          </DropdownItem>,
        )
      }
    } else {
      menuItems.push(
        <DropdownItem
          disabled={item.isSaving}
          key='change-type'
          onClick={this._saveChangeType}
        >
          Change to{' '}
          {item.type === FeedItemType.Post
            ? FeedItemType.Lead
            : FeedItemType.Post}
        </DropdownItem>,
      )
    }

    if (this._canDelete()) {
      menuItems.push(
        <DropdownItem
          disabled={item.isSaving}
          key='delete'
          onClick={this._deleteFeedItem}
        >
          Delete
        </DropdownItem>,
      )
    }

    if (this._canArchive()) {
      menuItems.push(
        <DropdownItem
          disabled={item.isSaving}
          key='archive'
          onClick={this._archiveLead}
        >
          Archive
        </DropdownItem>,
      )
    }

    return <DropdownMenu right>{menuItems}</DropdownMenu>
  }

  private _saveChangeType() {
    const newType =
      this.props.item.type === FeedItemType.Post
        ? FeedItemType.Lead
        : FeedItemType.Post

    this.props.saveChangeType(this.props.item.id, newType)
  }

  private _share() {
    this.props.showShareFeedItemModal(this.props.item.id)
  }
}
